import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../shared/auth/AuthContext';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';
import { formatCurrency, formatDate } from '../../shared/utils/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  LoadingSkeleton,
} from '@nexora/design-system';

/* ── Contratos reais (backend/.review-loop/reports/served-routes.json) ──
 * GET /finance/receivables/dashboard → ReceivableDashboardDto
 *   { aging{ current, days30, days60, days90, days120plus, total },
 *     agingBuckets[AgingBucketDto], upcoming[UpcomingInvoiceItemDto],
 *     summary{ totalOpen, overdueTotal, dueNext7Days, totalReceived30d, ... } }
 * (DTOs em backend/.../dto/treasury.dto.ts)
 */

interface AgingBucket {
  label: string;
  value: number;
  count: number;
}

interface UpcomingInvoiceItem {
  id: string;
  customerName: string;
  documentNumber: string;
  dueDate: string;
  value: number;
  balance: number;
  status: string;
  daysToDue: number;
}

interface ReceivableDashboard {
  aging: {
    current: number;
    days30: number;
    days60: number;
    days90: number;
    days120plus: number;
    total: number;
  };
  agingBuckets: AgingBucket[];
  upcoming: UpcomingInvoiceItem[];
  summary: {
    totalOpen: number;
    totalOpenCount: number;
    overdueTotal: number;
    overdueCount: number;
    dueNext7Days: number;
    dueNext7DaysCount: number;
    totalReceived30d: number;
  };
}

function statusBadge(status: string) {
  const map: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    OPEN: { variant: 'info', label: 'Em aberto' },
    PARTIALLY_SETTLED: { variant: 'warning', label: 'Parcial' },
    SETTLED: { variant: 'success', label: 'Liquidado' },
    OVERDUE: { variant: 'error', label: 'Vencido' },
    RENEGOTIATED: { variant: 'warning', label: 'Renegociado' },
    CANCELLED: { variant: 'neutral', label: 'Cancelado' },
  };
  return map[status] ?? { variant: 'neutral' as const, label: status };
}

/** Barras horizontais do aging — SVG inline, padrão do dashboard financeiro. */
function AgingBars({ buckets }: { buckets: AgingBucket[] }): JSX.Element {
  const W = 640;
  const rowH = 36;
  const H = buckets.length * rowH + 16;
  const max = Math.max(1, ...buckets.map((b) => b.value));
  const labelW = 120;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="w-full h-auto"
      role="img"
      aria-label={`Aging: ${buckets.map((b) => `${b.label} ${formatCurrency(b.value)}`).join('; ')}.`}
    >
      {buckets.map((b, i) => {
        const y = 8 + i * rowH;
        const w = Math.max(3, ((W - labelW - 150) * b.value) / max);
        return (
          <g key={b.label}>
            <title>{`${b.label}: ${formatCurrency(b.value)} em ${b.count} títulos`}</title>
            <text x={0} y={y + 15} fontSize={11} fill="#43474e">
              {b.label}
            </text>
            <rect x={labelW} y={y} width={w} height={18} rx={5} fill={i >= 3 ? '#ba1a1a' : i >= 1 ? '#c47e1a' : '#2e7d32'} />
            <text x={labelW + w + 8} y={y + 15} fontSize={11} fill="#43474e">
              {formatCurrency(b.value)}
              {b.count > 0 ? ` · ${b.count}` : ''}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function ReceivablesDashboardPage(): JSX.Element {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? '';

  const dashboardQ = useQuery<ReceivableDashboard>({
    queryKey: queryKeys.finance.dashboard(tenantId).concat('receivables' as const),
    queryFn: async () => apiClient.get<ReceivableDashboard>('/finance/receivables/dashboard'),
    enabled: !!tenantId,
  });

  const data = dashboardQ.data;

  return (
    <div className="flex-1 overflow-auto bg-surface-bright p-md md:p-lg">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Contas a Receber</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Visão geral financeira e controle de recebimentos.
          </p>
        </div>
      </div>

      {dashboardQ.isLoading && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
          <LoadingSkeleton />
          <span style={{ fontSize: '0.875rem' }}>Carregando dashboard de contas a receber...</span>
        </div>
      )}

      {dashboardQ.error && !dashboardQ.isLoading && (
        <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
          <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar dashboard</p>
          <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>{(dashboardQ.error as Error).message}</p>
        </div>
      )}

      {!dashboardQ.isLoading && !dashboardQ.error && data && (
        <>
          {/* KPIs — espelha o protótipo (Total, Overdue, A receber 30d) */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-md mb-lg">
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Total em aberto</span>
              <p className="font-headline-md text-headline-md text-on-surface m-0 mt-sm tabular-nums">{formatCurrency(data.summary.totalOpen)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.totalOpenCount} títulos</p>
            </div>
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Vencido</span>
              <p className="font-headline-md text-headline-md text-error m-0 mt-sm tabular-nums">{formatCurrency(data.summary.overdueTotal)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.overdueCount} títulos vencidos</p>
            </div>
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Vence em 7 dias</span>
              <p className="font-headline-md text-headline-md text-warning-amber m-0 mt-sm tabular-nums">{formatCurrency(data.summary.dueNext7Days)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">{data.summary.dueNext7DaysCount} títulos</p>
            </div>
            <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-md shadow-sm">
              <span className="font-label-md text-label-md text-on-surface-variant uppercase tracking-wider">Recebido (30 dias)</span>
              <p className="font-headline-md text-headline-md text-success-green m-0 mt-sm tabular-nums">{formatCurrency(data.summary.totalReceived30d)}</p>
              <p className="font-body-sm text-body-sm text-on-surface-variant mt-xs">últimos 30 dias</p>
            </div>
          </div>

          {/* Aging + Upcoming */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-md">
            <Card variant="outlined" padding="md">
              <CardHeader>
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Aging Report</h3>
              </CardHeader>
              <div className="overflow-x-auto">
                <AgingBars buckets={data.agingBuckets} />
              </div>
            </Card>

            <Card variant="outlined" padding="0" className="lg:col-span-2">
              <CardHeader className="px-md pt-md">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Próximas Faturas</h3>
              </CardHeader>
              {data.upcoming.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#74777f' }}>
                  Nenhuma fatura pendente.
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        {['Cliente', 'Valor', 'Vencimento', 'Status', 'Ações'].map((h) => (
                          <th
                            key={h}
                            style={{
                              textAlign: h === 'Valor' || h === 'Ações' ? 'right' : 'left',
                              padding: '0.75rem 1rem',
                              background: '#f2f4f6',
                              color: '#43474e',
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: '2px solid #e0e3e5',
                              whiteSpace: 'nowrap',
                              position: 'sticky',
                              top: 0,
                              zIndex: 1,
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {data.upcoming.map((u) => (
                        <tr key={u.id} style={{ transition: 'background 0.15s ease' }}>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 500 }}>
                            {u.customerName}
                            <span style={{ display: 'block', fontSize: '12px', color: '#74777f' }}>Doc: {u.documentNumber}</span>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', fontWeight: 600, textAlign: 'right', fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontVariantNumeric: 'tabular-nums' }}>
                            {formatCurrency(u.balance)}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', whiteSpace: 'nowrap' }}>
                            {formatDate(u.dueDate)}
                            {u.daysToDue <= 0 && <Badge variant="error" size="sm" dot>vencido</Badge>}
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0' }}>
                            <Badge variant={statusBadge(u.status).variant} size="sm" dot>{statusBadge(u.status).label}</Badge>
                          </td>
                          <td style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eceef0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <Button variant="ghost" size="sm">Baixar</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </div>
        </>
      )}
    </div>
  );
}